import { useState } from 'react';
import toast from 'react-hot-toast';
import { Modal } from '../../components/Modal.tsx';
import { Button, Chip } from '../../components/ui.tsx';
import {
  useGroups,
  useCreateGroup,
  useDeleteGroup,
  useSetGroupMembers,
  useSetGroupOverlayState,
  groupOverlayState,
  type GroupOverlayState,
} from '../../api/groups.ts';
import { useScreens } from '../../api/screens.ts';
import { useOverlays } from '../../api/overlays.ts';
import { ApiError } from '../../api/client.ts';

// Sentinel <option> value for the explicit "no overlay" choice — distinct from ''
// (inherit) and from any real overlay id.
const NONE_VALUE = '__none__';

function overlaySelectValue(state: GroupOverlayState): string {
  if (state.kind === 'overlay') return state.overlay_id;
  if (state.kind === 'none') return NONE_VALUE;
  return '';
}

function overlayStateFromValue(value: string): GroupOverlayState {
  if (value === NONE_VALUE) return { kind: 'none' };
  if (value === '') return { kind: 'inherit' };
  return { kind: 'overlay', overlay_id: value };
}

/** Inline group management — labels, not a concept to study (docs/06). One modal deep. */
export function GroupsModal({ onClose }: { onClose: () => void }) {
  const groups = useGroups();
  const screens = useScreens();
  const overlays = useOverlays();
  const createGroup = useCreateGroup();
  const deleteGroup = useDeleteGroup();
  const setMembers = useSetGroupMembers();
  const setOverlay = useSetGroupOverlayState();
  const [newName, setNewName] = useState('');

  const allScreens = screens.data ?? [];
  const allOverlays = overlays.data ?? [];

  function toggleMember(groupId: string, current: string[], screenId: string) {
    const next = current.includes(screenId)
      ? current.filter((id) => id !== screenId)
      : [...current, screenId];
    setMembers.mutate(
      { id: groupId, body: { screen_ids: next } },
      { onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Update failed') },
    );
  }

  return (
    <Modal title="Manage groups" onClose={onClose} size="lg" footer={<Button variant="primary" onClick={onClose}>Done</Button>}>
      <form
        className="row mb-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!newName.trim()) return;
          createGroup.mutate(
            { name: newName.trim() },
            {
              onSuccess: () => setNewName(''),
              onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Failed'),
            },
          );
        }}
      >
        <input
          aria-label="New group name"
          placeholder="New group name (e.g. Storefront)"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
        />
        <Button variant="primary" type="submit" disabled={!newName.trim() || createGroup.isPending}>
          Add
        </Button>
      </form>

      {(groups.data ?? []).length === 0 ? (
        <p className="text-dim">No groups yet. Groups are labels you can schedule against.</p>
      ) : (
        (groups.data ?? []).map((g) => (
          <div key={g.id} className="card card-pad mb-4">
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <strong>{g.name}</strong>
              <Button
                size="sm"
                variant="danger"
                onClick={() =>
                  deleteGroup.mutate(g.id, {
                    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
                  })
                }
              >
                Delete
              </Button>
            </div>
            <div className="text-dim mt-2" style={{ fontSize: 'var(--fs-sm)' }}>
              {g.screen_ids.length} screen{g.screen_ids.length === 1 ? '' : 's'} · click to toggle
            </div>
            <div className="chip-row mt-2">
              {allScreens.map((s) => (
                <Chip
                  key={s.id}
                  selected={g.screen_ids.includes(s.id)}
                  onClick={() => toggleMember(g.id, g.screen_ids, s.id)}
                >
                  {s.name}
                </Chip>
              ))}
            </div>
            {/* Per-group overlay (docs/09 §C2), tri-state: inherit the org default,
                a specific overlay, or an explicit "no overlay" that stops resolution
                at NONE (the org default is NOT applied). A screen's own overlay still
                wins over the group. */}
            <div className="group-overlay-row mt-2">
              <label htmlFor={`grp-overlay-${g.id}`}>Overlay</label>
              <select
                id={`grp-overlay-${g.id}`}
                value={overlaySelectValue(groupOverlayState(g))}
                disabled={setOverlay.isPending}
                onChange={(e) =>
                  setOverlay.mutate(
                    { id: g.id, state: overlayStateFromValue(e.target.value) },
                    { onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Update failed') },
                  )
                }
              >
                <option value="">Inherit org default</option>
                <option value={NONE_VALUE}>No overlay (ignore org default)</option>
                {allOverlays.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ))
      )}
    </Modal>
  );
}
